import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";

import EmployeeGradeService from "../services/employeeGrade.service";
import { IEmployeeGradeState } from "../types";
import { StoreState } from "@/store/type";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { formatString } from "@/core/utils/helpers";
import analyticsService from "@/analytic.service";
import { EMPLOYEE_GRADE_ANALYTICS_EVENTS } from "../constants/analyticsEvents";
import {
  CreateEmployeeGradeDto,
  UpdateEmployeeGradeDto,
} from "../dtos/employeeGrade.dtos";

const employeeGradeService = new EmployeeGradeService();

const actions: ActionTree<IEmployeeGradeState, StoreState> = {
  async fetchEmployeeGrades(
    context: ActionContext<IEmployeeGradeState, StoreState>,
    payload: { organizationId: string; isActive?: boolean },
  ) {
    try {
      context.commit("setFetchingEmployeeGrades", true);
      const { organizationId, isActive } = payload;
      const response: ICoreResponse =
        await employeeGradeService.getEmployeeGrades(organizationId, isActive);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setEmployeeGrades", response.data);
        analyticsService.logActionEvent(
          EMPLOYEE_GRADE_ANALYTICS_EVENTS.EMPLOYEE_GRADE_FETCH,
          { organizationId, isActive },
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setEmployeeGrades", []);
    } finally {
      context.commit("setFetchingEmployeeGrades", false);
    }
  },

  async createEmployeeGrade(
    context: ActionContext<IEmployeeGradeState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: CreateEmployeeGradeDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setEmployeeGradeActionLoading", true);
      const response: ICoreResponse =
        await employeeGradeService.createEmployeeGrade(organizationId, body);
      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        successHandler();
        analyticsService.logActionEvent(
          EMPLOYEE_GRADE_ANALYTICS_EVENTS.EMPLOYEE_GRADE_CREATE,
          { organizationId, name: body.name },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.EMPLOYEE_GRADE_CREATE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setEmployeeGradeActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async updateEmployeeGrade(
    context: ActionContext<IEmployeeGradeState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: UpdateEmployeeGradeDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setEmployeeGradeActionLoading", true);
      const response: ICoreResponse =
        await employeeGradeService.updateEmployeeGrade(organizationId, body);
      if (response.success && response.status === StatusCodes.OK) {
        successHandler();
        analyticsService.logActionEvent(
          EMPLOYEE_GRADE_ANALYTICS_EVENTS.EMPLOYEE_GRADE_UPDATE,
          { organizationId, publicId: body.public_id, name: body.name },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.EMPLOYEE_GRADE_UPDATE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setEmployeeGradeActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async deleteEmployeeGrade(
    context: ActionContext<IEmployeeGradeState, StoreState>,
    {
      organizationId,
      publicId,
      successHandler,
    }: {
      organizationId: string;
      publicId: string;
      successHandler: () => void;
    },
  ) {
    try {
      context.commit("setEmployeeGradeActionLoading", true);
      const response: ICoreResponse =
        await employeeGradeService.deleteEmployeeGrade(
          organizationId,
          publicId,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedGrades = context.state.employeeGrades.filter(
          (grade) => grade.public_id !== publicId,
        );
        context.commit("setEmployeeGrades", updatedGrades);
        successHandler();
        analyticsService.logActionEvent(
          EMPLOYEE_GRADE_ANALYTICS_EVENTS.EMPLOYEE_GRADE_DELETE,
          { organizationId, publicId },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.EMPLOYEE_GRADE_DELETE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setEmployeeGradeActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async patchEmployeeGradeStatus(
    context: ActionContext<IEmployeeGradeState, StoreState>,
    {
      organizationId,
      employeeGradeId,
      employeeGradeName,
      isActive,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      employeeGradeId: string;
      employeeGradeName: string;
      isActive: boolean;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      context.commit("setEmployeeGradeActionLoading", true);
      const response: ICoreResponse =
        await employeeGradeService.patchEmployeeGradeStatus(
          organizationId,
          employeeGradeId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedGrades = context.state.employeeGrades.map((grade) => {
          if (grade.public_id === employeeGradeId) {
            return { ...grade, is_active: isActive };
          }
          return grade;
        });
        context.commit("setEmployeeGrades", updatedGrades);
        if (successHandler) {
          successHandler();
        }
        analyticsService.logActionEvent(
          EMPLOYEE_GRADE_ANALYTICS_EVENTS.EMPLOYEE_GRADE_STATUS_TOGGLE,
          { organizationId, employeeGradeId, employeeGradeName, isActive },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.EMPLOYEE_GRADE_STATUS_UPDATE_SUCCESS,
          employeeGradeName,
          isActive ? "active" : "inactive",
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      if (errorHandler) {
        errorHandler();
      }
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setEmployeeGradeActionLoading", false);
      notificationService.triggerNotification();
    }
  },
};

export default actions;
